"use client";

import { CSSProperties, useId, useState } from "react";
import { cn } from "@/lib/utils";
import { useReveal } from "../kit/useReveal";

/**
 * One FAQ card (Figma 12375:10481 open / 12375:10486 closed; mobile 12405:8341 / 8346).
 * Card: rgba(150,150,150,.05) → .1 when open (prototype `.faq details` background .25s), radius 4, padding 24.
 * Row: question (Geist 500, 18/24 desktop · 16/24 mobile, black 90%) + 28×28 plus/minus icon, gap 24.
 * Answer: Geist 400 16/24 black 70%, 4px under the row; opens/closes with a grid-rows transition.
 * Toggles are independent (prototype `<details>`); the first card renders open on the server (Figma resting state).
 * Entrance: prototype `.rv-f` fade/rise with `--d = min(4, index) × 90ms`.
 */
export default function FaqItem({ question, answer, index, defaultOpen = false }: { question?: string; answer?: string; index: number; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [ref, on] = useReveal<HTMLDivElement>();
  const id = useId();
  const qId = `${id}q`;
  const aId = `${id}a`;

  return (
    <div ref={ref} className={cn("qe-rv", on && "qe-in")} style={{ "--d": `${Math.min(4, index) * 90}ms` } as CSSProperties}>
      <div
        data-open={open}
        className="rounded-[0.5rem] bg-[rgba(150,150,150,0.05)] p-[3rem] transition-colors duration-[250ms] data-[open=true]:bg-[rgba(150,150,150,0.1)] motion-reduce:transition-none"
      >
        <h3 className="mb-0 font-geist text-[2rem] leading-[1.5] font-medium tracking-normal text-black/90 lg:text-[2.25rem] lg:leading-[1.3333]">
          <button
            type="button"
            id={qId}
            aria-expanded={open}
            aria-controls={aId}
            onClick={() => setOpen((v) => !v)}
            className="flex w-full cursor-pointer appearance-none items-center gap-[3rem] border-0 bg-transparent p-0 text-left [color:inherit] [font:inherit] [letter-spacing:inherit] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#51a667]"
          >
            <span className="min-w-0 flex-1 font-geist">{question}</span>
            <span aria-hidden="true" className="relative block h-[3.5rem] w-[3.5rem] shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/quarterly-event/faq/icon-add.svg" alt="" className={cn("absolute inset-0 h-full w-full", open && "hidden")} />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/quarterly-event/faq/icon-subtract.svg" alt="" className={cn("absolute inset-0 h-full w-full", !open && "hidden")} />
            </span>
          </button>
        </h3>
        {answer && (
          <div
            id={aId}
            role="region"
            aria-labelledby={qId}
            aria-hidden={!open}
            className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
            style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
          >
            <div className="min-h-0 overflow-hidden">
              <p className="mb-0 pt-[0.5rem] font-geist text-[2rem] leading-[1.5] font-normal whitespace-pre-line text-black/70">{answer}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
